import {
    type ConfigGetParams,
    type ConfigGetResult,
    type ConfigGetSchemaParams,
    type ConfigOwner,
    type ConfigSchemaResult,
    type ConfigTarget,
    type ConfigValueMeta,
    type ConfigValueSource,
    createDiagnostic,
    deleteNestedValue,
    EDITOR_SETTINGS_REGISTRY,
    getNestedValue,
    hasNestedValue,
    type SettingsCapabilities,
    type SettingsRegistryEntry,
    type SettingsSnapshot,
    setNestedValue,
    validateValueAgainstSchema,
} from "@magic-md/engine-client/config";
import type { InitializeResult } from "@magic-md/engine-client/protocol";
import { t as translate } from "@magic-md/i18n";
import { waitForEngineReady } from "../components/settings/settings-center/resolvers";
import type { EngineService } from "../platform";

function tr(key: string): string {
    return translate("editor", key);
}

const SOURCE_PRIORITY: Record<ConfigValueSource, number> = {
    default: 0,
    user: 1,
    workspace: 2,
    remoteHost: 3,
    env: 4,
    cli: 5,
};

const USER_CAPABLE_OWNERS = new Set<ConfigOwner>(["editor", "extension", "plugin"]);

export interface SettingsCommitParams {
    owner: ConfigOwner;
    target: ConfigTarget;
    dotPath?: string;
    value?: unknown;
    patch?: Record<string, unknown>;
    unset?: string[];
}

export interface SettingsReloadParams {
    target?: ConfigTarget;
}

export interface UserSettingsStorage {
    load(): Promise<{
        data: Record<string, unknown>;
        filePath?: string | null;
        diagnostics?: ConfigGetResult["diagnostics"];
    }>;
    save(data: Record<string, unknown>): Promise<void>;
}

export interface UserSettingsAdapter {
    getSchema(
        params?: ConfigGetSchemaParams,
        registryEntries?: SettingsRegistryEntry[],
    ): Promise<ConfigSchemaResult>;
    getSnapshot(
        params?: ConfigGetParams,
        registryEntries?: SettingsRegistryEntry[],
    ): Promise<ConfigGetResult>;
    setValue(
        params: SettingsCommitParams,
        registryEntries?: SettingsRegistryEntry[],
    ): Promise<void>;
    reload(): Promise<void>;
    capabilities(): SettingsCapabilities;
}

export interface SettingsService {
    getSchema(
        params?: ConfigGetSchemaParams,
        registryEntries?: SettingsRegistryEntry[],
    ): Promise<ConfigSchemaResult>;
    getSnapshot(
        params?: ConfigGetParams,
        registryEntries?: SettingsRegistryEntry[],
    ): Promise<SettingsSnapshot>;
    setValue(
        params: SettingsCommitParams,
        registryEntries?: SettingsRegistryEntry[],
    ): Promise<void>;
    reload(params?: SettingsReloadParams): Promise<void>;
    capabilities(): SettingsCapabilities;
}

export interface CreateSettingsServiceOptions {
    getActive: () => EngineService;
    subscribe?: (listener: (newService: EngineService) => void) => () => void;
    userAdapter: UserSettingsAdapter;
    clientInfo?: {
        name: string;
        version: string;
    };
}

export function createEditorUserSettingsAdapter(options: {
    storage: UserSettingsStorage;
}): UserSettingsAdapter {
    const capabilities: SettingsCapabilities = {
        availableTargets: ["user"],
        owners: {
            editor: {
                readableTargets: ["user"],
                writableTargets: ["user"],
            },
            plugin: {
                readableTargets: ["user"],
                writableTargets: ["user"],
            },
            extension: {
                readableTargets: ["user"],
                writableTargets: ["user"],
            },
        },
    };

    async function loadState(entries: SettingsRegistryEntry[]): Promise<{
        data: Record<string, unknown>;
        filePath?: string | null;
        diagnostics: ConfigGetResult["diagnostics"];
        view: ConfigGetResult["view"];
        meta: ConfigGetResult["meta"];
    }> {
        const stored = await options.storage.load();
        const diagnostics: ConfigGetResult["diagnostics"] = [
            ...(stored.diagnostics ?? []),
        ];
        const viewValues: Record<string, unknown> = {};
        const metaEntries: ConfigGetResult["meta"]["entries"] = {};

        for (const entry of entries) {
            const explicitValue = hasNestedValue(stored.data, entry.key)
                ? getNestedValue(stored.data, entry.key)
                : undefined;

            let source: ConfigValueSource = "default";
            let value = clone(entry.defaultValue);
            const setInTargets: Partial<Record<ConfigTarget, boolean>> = {};
            const targetValues: Partial<Record<ConfigTarget, unknown>> = {};

            if (explicitValue !== undefined) {
                const error = validateValueAgainstSchema(explicitValue, entry.schema);
                if (error) {
                    diagnostics.push(
                        createDiagnostic(error, {
                            code: "config.invalidType",
                            severity: "error",
                            key: entry.key,
                            owner: entry.owner,
                            target: "user",
                            filePath: stored.filePath ?? undefined,
                        }),
                    );
                } else {
                    value = clone(explicitValue);
                    source = "user";
                    setInTargets.user = true;
                    targetValues.user = clone(explicitValue);
                }
            }

            if (value !== undefined) {
                viewValues[entry.key] = value;
            }
            metaEntries[entry.key] = {
                source,
                effect: entry.effect,
                setInTargets,
                targetValues,
            };
        }

        return {
            data: stored.data,
            filePath: stored.filePath,
            diagnostics,
            view: { values: viewValues },
            meta: { entries: metaEntries },
        };
    }

    return {
        async getSchema(params, registryEntries) {
            return {
                entries: buildUserRegistryEntries(params?.owner, registryEntries),
            };
        },

        async getSnapshot(params, registryEntries) {
            const entries = buildUserRegistryEntries(params?.owner, registryEntries);
            if (entries.length === 0) {
                return {
                    view: { values: {} },
                    meta: { entries: {} },
                    diagnostics: [],
                    filePaths: {},
                    ownerFilePaths: {},
                };
            }

            const state = await loadState(entries);
            const ownerFilePaths = Object.fromEntries(
                [...new Set(entries.map((entry) => entry.owner))].map((owner) => [
                    owner,
                    { user: state.filePath ?? null },
                ]),
            ) as ConfigGetResult["ownerFilePaths"];
            return {
                view: state.view,
                meta: state.meta,
                diagnostics: state.diagnostics,
                filePaths: {
                    user: state.filePath ?? null,
                },
                ownerFilePaths,
            };
        },

        async setValue(params, registryEntries) {
            if (params.target !== "user") {
                throw new Error(
                    "User settings adapter only supports user-target writes",
                );
            }

            const entries = buildUserRegistryEntries(params.owner, registryEntries);
            const current = await options.storage.load();
            const nextData = clone(current.data);
            const changeSet = buildChangeSet(params);
            const affectedKeys = [
                ...Object.keys(changeSet.patch),
                ...changeSet.unset,
            ];

            for (const key of affectedKeys) {
                const entry = entries.find((item) => item.key === key);
                if (!entry) {
                    throw new Error(`Unknown config key: ${key}`);
                }
                const candidate = changeSet.patch[key];
                if (candidate !== undefined) {
                    const error = validateValueAgainstSchema(candidate, entry.schema);
                    if (error) {
                        throw new Error(error);
                    }
                }
            }

            for (const [key, value] of Object.entries(changeSet.patch)) {
                setNestedValue(nextData, key, value);
            }
            for (const key of changeSet.unset) {
                deleteNestedValue(nextData, key);
            }

            if (Object.keys(nextData).length > 0) {
                if (!isPlainObject(nextData.meta)) {
                    nextData.meta = {};
                }
                setNestedValue(nextData, "meta.schemaVersion", 1);
            }

            await options.storage.save(stripEmptyMeta(nextData));
        },

        async reload() {
            // Storage backends are read fresh on every request, so reload is a no-op.
        },

        capabilities() {
            return capabilities;
        },
    };
}

export function createSettingsService({
    getActive,
    subscribe,
    userAdapter,
    clientInfo = { name: "magic-md-settings", version: "0.1.0" },
}: CreateSettingsServiceOptions): SettingsService {
    let cachedCapabilities = userAdapter.capabilities();
    let cachedInitEngine: EngineService | null = null;
    let cachedInitPromise: Promise<InitializeResult | null> | null = null;

    subscribe?.(() => {
        cachedInitEngine = null;
        cachedInitPromise = null;
        cachedCapabilities = userAdapter.capabilities();
    });

    async function getRemoteInitialize(): Promise<InitializeResult | null> {
        const engine = getActive();
        if (cachedInitEngine === engine && cachedInitPromise) {
            return cachedInitPromise;
        }

        cachedInitEngine = engine;
        cachedInitPromise = (async () => {
            await waitForEngineReady(engine);
            const result = await engine.initialize(clientInfo).catch(() => null);
            if (
                (!result || !result.capabilities.config) &&
                cachedInitEngine === engine
            ) {
                cachedInitPromise = null;
            }
            return result;
        })();
        return cachedInitPromise;
    }

    async function getRemoteSchema(
        params?: ConfigGetSchemaParams,
    ): Promise<ConfigSchemaResult> {
        const engine = getActive();
        if (!engine.configGetSchema) {
            return { entries: [] };
        }

        const init = await getRemoteInitialize();
        if (!init?.capabilities.config?.schema) {
            return { entries: [] };
        }

        try {
            return await engine.configGetSchema(params ?? {});
        } catch {
            return { entries: [] };
        }
    }

    async function getRemoteSnapshot(
        params?: ConfigGetParams,
    ): Promise<ConfigGetResult> {
        const engine = getActive();
        if (!engine.configGet) {
            return {
                view: { values: {} },
                meta: { entries: {} },
                diagnostics: [],
            };
        }

        const init = await getRemoteInitialize();
        if (!init?.capabilities.config?.read) {
            return {
                view: { values: {} },
                meta: { entries: {} },
                diagnostics: [],
            };
        }

        try {
            return await engine.configGet(params ?? {});
        } catch {
            return {
                view: { values: {} },
                meta: { entries: {} },
                diagnostics: [],
            };
        }
    }

    async function getRemoteCapabilities(): Promise<SettingsCapabilities> {
        const init = await getRemoteInitialize();
        return capabilitiesFromInitialize(init);
    }

    return {
        async getSchema(params, registryEntries) {
            const remoteSchema = await getRemoteSchema(params);
            const schemaWithAdditional = mergeSchemaResults(remoteSchema, {
                entries: filterRegistryEntries(params?.owner, registryEntries),
            });
            const userSchema = await userAdapter.getSchema(
                params,
                schemaWithAdditional.entries,
            );
            return mergeSchemaResults(schemaWithAdditional, userSchema);
        },

        async getSnapshot(params, registryEntries) {
            const [remoteSchema, remoteSnapshot, remoteCapabilities] =
                await Promise.all([
                    getRemoteSchema(params),
                    getRemoteSnapshot(params),
                    getRemoteCapabilities(),
                ]);
            const remoteSchemaWithAdditional = mergeSchemaResults(remoteSchema, {
                entries: filterRegistryEntries(params?.owner, registryEntries),
            });
            const [userSchema, userSnapshot] = await Promise.all([
                userAdapter.getSchema(params, remoteSchemaWithAdditional.entries),
                userAdapter.getSnapshot(params, remoteSchemaWithAdditional.entries),
            ]);
            const mergedSchema = mergeSchemaResults(
                remoteSchemaWithAdditional,
                userSchema,
            );
            const resolvedRemoteCapabilities = deriveRemoteCapabilities(
                remoteSchema.entries,
                remoteSnapshot,
                remoteCapabilities,
            );
            const capabilities = mergeCapabilities(
                resolvedRemoteCapabilities,
                userAdapter.capabilities(),
            );
            cachedCapabilities = capabilities;
            return mergeSnapshots(
                remoteSnapshot,
                userSnapshot,
                capabilities,
                mergedSchema.entries,
                params?.owner,
            );
        },

        async setValue(params, registryEntries) {
            if (params.target === "user") {
                const remoteSchema = await getRemoteSchema({
                    owner: params.owner,
                });
                const schemaWithAdditional = mergeSchemaResults(remoteSchema, {
                    entries: filterRegistryEntries(params.owner, registryEntries),
                });
                await userAdapter.setValue(params, schemaWithAdditional.entries);
                return;
            }

            const engine = getActive();
            if (!engine.configSet) {
                throw new Error("Remote config API not available");
            }

            const init = await getRemoteInitialize();
            const [remoteSchema, remoteSnapshot, fallbackCapabilities] =
                await Promise.all([
                    getRemoteSchema({ owner: params.owner }),
                    getRemoteSnapshot({ owner: params.owner }),
                    Promise.resolve(capabilitiesFromInitialize(init)),
                ]);
            const remoteSchemaWithAdditional = mergeSchemaResults(remoteSchema, {
                entries: filterRegistryEntries(params.owner, registryEntries),
            });
            const resolvedCapabilities = deriveRemoteCapabilities(
                remoteSchemaWithAdditional.entries,
                remoteSnapshot,
                fallbackCapabilities,
            );
            const ownerCapability = resolvedCapabilities.owners[params.owner];
            if (
                !ownerCapability ||
                !ownerCapability.writableTargets.includes(params.target)
            ) {
                throw new Error(tr("settingsService.targetWriteUnsupported"));
            }

            await engine.configSet({
                owner: params.owner,
                target: params.target,
                dotPath: params.dotPath,
                value: params.value,
                patch: params.patch,
                unset: params.unset,
            });
        },

        async reload(params) {
            if (!params?.target || params.target === "user") {
                await userAdapter.reload();
            }

            if (!params?.target || params.target !== "user") {
                const engine = getActive();
                if (engine.configReload) {
                    await engine.configReload(
                        params?.target && params.target !== "user"
                            ? { target: params.target }
                            : {},
                    );
                }
            }
        },

        capabilities() {
            return cachedCapabilities;
        },
    };
}

function mergeSnapshots(
    remoteSnapshot: ConfigGetResult,
    userSnapshot: ConfigGetResult,
    capabilities: SettingsCapabilities,
    schemaEntries: SettingsRegistryEntry[],
    owner?: ConfigOwner,
): SettingsSnapshot {
    const values = { ...(remoteSnapshot.view.values ?? {}) };
    const metaEntries = { ...(remoteSnapshot.meta.entries ?? {}) };

    for (const entry of schemaEntries) {
        if (!entry.targets.includes("user")) continue;
        if (owner && entry.owner !== owner) continue;

        const key = entry.key;
        const userMeta = userSnapshot.meta.entries[key] ?? createDefaultMeta(entry);
        const remoteMeta =
            remoteSnapshot.meta.entries[key] ?? createDefaultMeta(entry);
        const chosenSource =
            SOURCE_PRIORITY[remoteMeta.source] > SOURCE_PRIORITY[userMeta.source]
                ? remoteMeta.source
                : userMeta.source;
        const chosenMeta =
            chosenSource === userMeta.source &&
                SOURCE_PRIORITY[userMeta.source] > SOURCE_PRIORITY[remoteMeta.source]
                ? userMeta
                : remoteMeta;
        const chosenValue =
            chosenMeta === userMeta
                ? userSnapshot.view.values[key]
                : remoteSnapshot.view.values[key];
        const fallbackValue =
            chosenMeta === userMeta
                ? remoteSnapshot.view.values[key]
                : userSnapshot.view.values[key];

        if (chosenValue !== undefined || fallbackValue !== undefined) {
            values[key] = chosenValue ?? fallbackValue;
        }
        metaEntries[key] = {
            ...chosenMeta,
            effect: entry.effect ?? chosenMeta.effect,
            setInTargets: {
                ...(userMeta.setInTargets ?? {}),
                ...(remoteMeta.setInTargets ?? {}),
            },
            targetValues: {
                ...(userMeta.targetValues ?? {}),
                ...(remoteMeta.targetValues ?? {}),
            },
        };
    }

    return {
        view: {
            values,
            maskedKeys: dedupeStrings([
                ...(remoteSnapshot.view.maskedKeys ?? []),
                ...(userSnapshot.view.maskedKeys ?? []),
            ]),
        },
        meta: {
            entries: metaEntries,
        },
        diagnostics: [
            ...(remoteSnapshot.diagnostics ?? []),
            ...(userSnapshot.diagnostics ?? []),
        ],
        capabilities,
        filePaths: {
            user: userSnapshot.filePaths?.user ?? null,
            workspace: remoteSnapshot.filePaths?.workspace ?? null,
            remoteHost: remoteSnapshot.filePaths?.remoteHost ?? null,
        },
        ownerFilePaths: {
            ...(remoteSnapshot.ownerFilePaths ?? {}),
            ...(userSnapshot.ownerFilePaths ?? {}),
        },
    };
}

function mergeSchemaResults(
    remoteSchema: ConfigSchemaResult,
    userSchema: ConfigSchemaResult,
): ConfigSchemaResult {
    const byKey = new Map<string, SettingsRegistryEntry>();
    for (const entry of remoteSchema.entries) {
        byKey.set(entry.key, entry);
    }
    for (const entry of userSchema.entries) {
        const existing = byKey.get(entry.key);
        byKey.set(entry.key, existing ? mergeSchemaEntry(existing, entry) : entry);
    }
    return {
        entries: [...byKey.values()].sort((left, right) =>
            left.key.localeCompare(right.key),
        ),
    };
}

function filterRegistryEntries(
    owner: ConfigOwner | undefined,
    entries: SettingsRegistryEntry[] = [],
): SettingsRegistryEntry[] {
    return owner ? entries.filter((entry) => entry.owner === owner) : entries;
}

function mergeCapabilities(
    remoteCapabilities: SettingsCapabilities,
    userCapabilities: SettingsCapabilities,
): SettingsCapabilities {
    const owners: SettingsCapabilities["owners"] = {
        ...(remoteCapabilities.owners ?? {}),
    };

    for (const [owner, capability] of Object.entries(
        userCapabilities.owners ?? {},
    ) as Array<[ConfigOwner, SettingsCapabilities["owners"][ConfigOwner]]>) {
        if (!capability) continue;
        const existing = owners[owner];
        owners[owner] = {
            readableTargets: dedupeTargets([
                ...(existing?.readableTargets ?? []),
                ...capability.readableTargets,
            ]),
            writableTargets: dedupeTargets([
                ...(existing?.writableTargets ?? []),
                ...capability.writableTargets,
            ]),
        };
    }

    return {
        availableTargets: dedupeTargets([
            ...(remoteCapabilities.availableTargets ?? []),
            ...(userCapabilities.availableTargets ?? []),
        ]),
        owners,
    };
}

function deriveRemoteCapabilities(
    schemaEntries: SettingsRegistryEntry[],
    remoteSnapshot: ConfigGetResult,
    fallback: SettingsCapabilities,
): SettingsCapabilities {
    if (schemaEntries.length === 0) {
        return fallback;
    }

    const workspaceAvailable =
        remoteSnapshot.filePaths?.workspace != null ||
        Object.values(remoteSnapshot.ownerFilePaths ?? {}).some(
            (paths) => paths?.workspace != null,
        );
    const owners: SettingsCapabilities["owners"] = {};

    for (const entry of schemaEntries) {
        const readableTargets = entry.targets.filter(
            (target): target is Exclude<ConfigTarget, "user"> =>
                target !== "user" && (target !== "workspace" || workspaceAvailable),
        );
        const writableTargets = entry.writableTargets.filter(
            (target): target is Exclude<ConfigTarget, "user"> =>
                target !== "user" && (target !== "workspace" || workspaceAvailable),
        );
        if (readableTargets.length === 0 && writableTargets.length === 0) {
            continue;
        }
        const existing = owners[entry.owner] ?? {
            readableTargets: [],
            writableTargets: [],
        };
        owners[entry.owner] = {
            readableTargets: dedupeTargets([
                ...existing.readableTargets,
                ...readableTargets,
            ]),
            writableTargets: dedupeTargets([
                ...existing.writableTargets,
                ...writableTargets,
            ]),
        };
    }

    const availableTargets = dedupeTargets(
        [
            ...Object.values(owners).flatMap((owner) => owner?.readableTargets ?? []),
            ...Object.values(owners).flatMap((owner) => owner?.writableTargets ?? []),
        ].filter(
            (target): target is ConfigTarget =>
                target === "workspace" || target === "remoteHost",
        ),
    );

    if (availableTargets.length === 0) {
        return fallback;
    }

    return {
        availableTargets,
        owners,
    };
}

function capabilitiesFromInitialize(
    init: InitializeResult | null,
): SettingsCapabilities {
    const config = init?.capabilities.config;
    if (!config) {
        return {
            availableTargets: [],
            owners: {},
        };
    }

    const owners: SettingsCapabilities["owners"] = {};
    const availableTargets = new Set<ConfigTarget>();

    for (const [owner, capability] of Object.entries(config.owners) as Array<
        [ConfigOwner, NonNullable<(typeof config.owners)[ConfigOwner]>]
    >) {
        if (!capability) continue;
        const readableTargets = capability.readableTargets.map(
            (target: (typeof capability.readableTargets)[number]) =>
                target as ConfigTarget,
        );
        const writableTargets = capability.writableTargets.map(
            (target: (typeof capability.writableTargets)[number]) =>
                target as ConfigTarget,
        );
        for (const target of readableTargets) availableTargets.add(target);
        for (const target of writableTargets) availableTargets.add(target);
        owners[owner] = {
            readableTargets,
            writableTargets,
        };
    }

    return {
        availableTargets: [...availableTargets],
        owners,
    };
}

function buildChangeSet(params: SettingsCommitParams): {
    patch: Record<string, unknown>;
    unset: string[];
} {
    const patch = { ...(params.patch ?? {}) };
    if (params.dotPath) {
        patch[params.dotPath] = params.value;
    }
    return {
        patch,
        unset: [...(params.unset ?? [])],
    };
}

function createDefaultMeta(entry: SettingsRegistryEntry): ConfigValueMeta {
    return {
        source: "default",
        effect: entry.effect,
        setInTargets: {},
    };
}

function buildUserRegistryEntries(
    owner: ConfigOwner | undefined,
    registryEntries: SettingsRegistryEntry[] = [],
): SettingsRegistryEntry[] {
    const byKey = new Map<string, SettingsRegistryEntry>();

    for (const entry of EDITOR_SETTINGS_REGISTRY) {
        const normalized = normalizeUserEntry(entry);
        if (!normalized) continue;
        if (owner && normalized.owner !== owner) continue;
        byKey.set(normalized.key, normalized);
    }

    for (const entry of registryEntries) {
        const normalized = normalizeUserEntry(entry);
        if (!normalized) continue;
        if (owner && normalized.owner !== owner) continue;
        const existing = byKey.get(normalized.key);
        byKey.set(
            normalized.key,
            existing ? mergeSchemaEntry(existing, normalized) : normalized,
        );
    }

    return [...byKey.values()].sort((left, right) =>
        left.key.localeCompare(right.key),
    );
}

function normalizeUserEntry(
    entry: SettingsRegistryEntry,
): SettingsRegistryEntry | null {
    if (!USER_CAPABLE_OWNERS.has(entry.owner)) {
        return null;
    }
    return {
        ...entry,
        targets: dedupeTargets([...entry.targets, "user"]),
        writableTargets: dedupeTargets([...entry.writableTargets, "user"]),
    };
}

function mergeSchemaEntry(
    existing: SettingsRegistryEntry,
    incoming: SettingsRegistryEntry,
): SettingsRegistryEntry {
    return {
        ...existing,
        ...incoming,
        targets: dedupeTargets([...existing.targets, ...incoming.targets]),
        writableTargets: dedupeTargets([
            ...existing.writableTargets,
            ...incoming.writableTargets,
        ]),
    };
}

function stripEmptyMeta(
    data: Record<string, unknown>,
): Record<string, unknown> {
    if (
        isPlainObject(data.meta) &&
        Object.keys(data.meta as Record<string, unknown>).length === 0
    ) {
        delete data.meta;
    }
    return data;
}

function dedupeTargets(values: ConfigTarget[]): ConfigTarget[] {
    return [...new Set(values)];
}

function dedupeStrings(values: string[]): string[] {
    return [...new Set(values)];
}

function clone<T>(value: T): T {
    if (value === undefined) return value;
    return structuredClone(value);
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
    if (!value || typeof value !== "object" || Array.isArray(value)) return false;
    const proto = Object.getPrototypeOf(value);
    return proto === Object.prototype || proto === null;
}
